/**
 * The Quality run's `result` — an ALCOA+ scorecard per document.
 *
 * `SolutionRunDetailOut.result` is an untyped object in OpenAPI (its shape
 * depends on the run's kind), so this mirrors the backend Pydantic model
 * `QualityResult` in `backend/src/daquma/services/quality_alcoa.py`, which
 * validates every stored result. Keep the two in step.
 */

import type { Citation } from "@/features/solutions/shared";

export type QualityStatus = "pass" | "concern" | "fail" | "not_assessable";
export type Principle =
  | "attributable"
  | "legible"
  | "contemporaneous"
  | "original"
  | "accurate"
  | "complete"
  | "consistent"
  | "enduring"
  | "available";

export interface QualityFinding {
  description: string;
  /** Verbatim from the document — verified server-side before it is stored. */
  quote: string;
  page: number | null;
  suggested_fix: string;
  citation: Citation;
}

export interface QualityPrinciple {
  principle: Principle;
  status: QualityStatus;
  summary: string;
  findings: QualityFinding[];
  /** Findings dropped because their quote was not in the document. */
  dropped_findings: number;
}

export interface QualityDocument {
  document_id: string;
  index_id: string;
  display_name: string;
  source_kind: string;
  source_version: string | null;
  content_hash: string | null;
  overall: QualityStatus;
  assessed: boolean;
  error: string | null;
  page_count: number | null;
  sections: number;
  principles: QualityPrinciple[];
  notes: string[];
}

export interface QualityResult {
  prompt_version: string;
  principles: Principle[];
  documents: QualityDocument[];
  ai_inference_ids: string[];
}

export const PRINCIPLE_LABEL: Record<Principle, string> = {
  attributable: "Attributable",
  legible: "Legible",
  contemporaneous: "Contemporaneous",
  original: "Original",
  accurate: "Accurate",
  complete: "Complete",
  consistent: "Consistent",
  enduring: "Enduring",
  available: "Available",
};

export const STATUS_LABEL: Record<QualityStatus, string> = {
  pass: "Pass",
  concern: "Concern",
  fail: "Fail",
  not_assessable: "Not assessable",
};

export const STATUS_COLOR: Record<QualityStatus, string> = {
  pass: "green",
  concern: "yellow",
  fail: "red",
  not_assessable: "gray",
};

/** Narrow a run's untyped result to a Quality result (or null). */
export function asQualityResult(result: unknown): QualityResult | null {
  if (!result || typeof result !== "object") return null;
  const r = result as Partial<QualityResult>;
  return Array.isArray(r.documents) && Array.isArray(r.principles) ? (r as QualityResult) : null;
}

/** A document's verdict for one principle (not assessable when missing). */
export function statusOf(doc: QualityDocument, principle: Principle): QualityStatus {
  return doc.principles.find((p) => p.principle === principle)?.status ?? "not_assessable";
}
